import React, { useEffect, useMemo } from "react";
import cx from "classnames";
import { Dimension } from "@models/IDimension";
import { IParams } from "@models/IParams";
import { useParams } from "react-router-dom";
import { Icon } from "@foris/avocado-icons";
import {
  Heading,
  Loading,
  Table,
  TableColumn,
  CardNotification,
  RoundButton,
  Button,
  Tooltip,
  SkeletonBase,
} from "@foris/avocado-suite";
import { Header } from "@common/components";
import { LinkSelector } from "@modules/shared/selectors/Selectors";
import { Link } from "@models/ISchema";
import {
  getColumnCell,
  getHeadCell,
  getLinkCell,
  getMoreOrLessPillCell,
} from "@common/utils/table.util";
import Layout from "@common/layout/Layout";
import useGetDimension from "@dimensions/hooks/useGetDimension";
import useGetPackageSection from "../hooks/useGetPackageSection";
import PackageHeaderSubtitle from "../components/PackageHeaderSubtitle";
import useAssignLinks, { ErrorMessagesByType } from "../hooks/useAssignLinks";
import useLinksState from "../hooks/useLinksState";
import LinkAssignmentErrors, { isErrorMessage } from "../components/LinkAssignmentErrors";
import { useTranslation } from "react-i18next";
import DimensionsSearchSkeleton from "@components/dimensions-search-skeleton";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import css from "./packageLinkManagement.module.scss";

const getLeftActionColumn = (
  id: string,
  linksToAdd = {},
  linksToDelete = {},
  errorMessagesByType: ErrorMessagesByType[] = [],
) => {
  const hasError = errorMessagesByType.some(err => Object.keys(err.links).includes(id));

  if (hasError) {
    return (
      <Icon
        className={cx(css.icon, css.alert_icon)}
        name="alert-triangle"
        size="sm"
        color="neutral-70"
      />
    );
  }

  if (id in linksToDelete) {
    return (
      <Icon className={cx(css.icon, css.trashIcon)} name="trash" size="sm" color="neutral-70" />
    );
  }

  if (id in linksToAdd) {
    return (
      <Icon
        className={cx(css.icon, css.addIcon)}
        filled
        name="circle-plus"
        size="sm"
        color="neutral-70"
      />
    );
  }

  return "";
};

const PackageLinkManagement = () => {
  const { id, origin, scenario, workspace }: IParams = useParams();
  const { editorUrl, adaptedHomeUrl } = useNavigationUrl();
  const [dimension] = useGetDimension(Dimension.package);
  const {
    linksToAdd,
    linksToDelete,
    assignmentIds,
    deletionsIds,
    assignmentsWithMultipleGroups,
    handleResetChanges,
    setLinksToAdd,
    setLinksToDelete,
  } = useLinksState({
    packageId: id,
  });

  const { t, i18n } = useTranslation();

  const { isLoading, headerData, packageData, loadPackageData } = useGetPackageSection({
    resourceId: id,
    scenarioId: scenario,
    originId: origin,
    workspaceId: workspace,
    fetchWithlinks: true,
  });

  const {
    handleLinkAssignment,
    isLoading: isLoadingChanges,
    errorMessagesByType,
  } = useAssignLinks({
    scenarioId: scenario,
    originId: origin,
    assignments: assignmentIds,
    deletions: deletionsIds,
    onSavedChanges: () => {
      loadPackageData();
      handleResetChanges();
    },
  });

  const isEditionEnabled = !packageData?.isPublished;

  const tableColumns = useMemo(() => {
    return [
      {
        header: getHeadCell(t("search-results.dimension.link.table.header.code")),
        renderCell: ({ code, id }: Link) => (
          <div className={css.cellWithIcon}>
            {getLeftActionColumn(id, linksToAdd, linksToDelete)}
            {getLinkCell(editorUrl("link", id), null, code)}
          </div>
        ),
        desktopOptions: { headCellStyles: { minWidth: "80px" } },
      },
      {
        header: getHeadCell(t("search-results.dimension.link.table.header.course-name")),
        renderCell: ({ course }: Link) =>
          getLinkCell(editorUrl("course", course?.id), null, `${course?.code} - ${course?.name}`),
        desktopOptions: {
          headCellStyles: {
            minWidth: "80px",
          },
        },
      },
      {
        header: getHeadCell(t("search-results.dimension.link.table.header.campus")),
        renderCell: ({ course }: Link) =>
          getColumnCell(course?.curriculum?.program?.campus?.code || "-"),
        desktopOptions: {
          headCellStyles: {
            minWidth: "80px",
            width: "120px",
          },
        },
      },
      {
        header: getHeadCell(t("search-results.dimension.link.table.header.term")),
        renderCell: ({ bundle }: Link) => getColumnCell(bundle?.term?.code || "-"),
        desktopOptions: {
          headCellStyles: {
            minWidth: "80px",
            width: "120px",
          },
        },
      },
      {
        header: getHeadCell(t("package-link-management.table.header.groups")),
        renderCell: ({ groups }: Link) => getMoreOrLessPillCell(groups),
        desktopOptions: { headCellStyles: { minWidth: "80px", width: "250px" } },
      },
      {
        header: getHeadCell(t("search-results.dimension.link.table.header.sections-count")),
        renderCell: ({ sectionsCount }: Link) => getColumnCell(sectionsCount ?? "-"),
        desktopOptions: {
          headCellStyles: {
            minWidth: "80px",
            width: "160px",
          },
        },
      },
      {
        header: getHeadCell(t("search-results.dimension.link.table.header.total-blocks")),
        renderCell: ({ totalBlocks }: Link) => getColumnCell(totalBlocks ?? "-"),
        desktopOptions: {
          headCellStyles: {
            minWidth: "80px",
            width: "140px",
          },
        },
      },
      {
        header: getHeadCell(t("search-results.dimension.link.table.header.vacancies")),
        renderCell: ({ vacancies }: Link) => getColumnCell(vacancies ?? "-"),
        desktopOptions: {
          headCellStyles: {
            minWidth: "80px",
            width: "124px",
          },
        },
      },
    ] as TableColumn[];
  }, [i18n.language, linksToAdd, linksToDelete]);

  const hasErrors = useMemo(() => {
    return errorMessagesByType.some(err => isErrorMessage(err.type, err.userCanSkipValidations));
  }, [errorMessagesByType]);

  useEffect(() => {
    loadPackageData();
  }, []);

  const linksToAvoidInSelector = useMemo(() => {
    return packageData?.links?.map(link => link.id) || [];
  }, [packageData?.links]);

  const packagesLinks = useMemo(() => {
    return [...(packageData?.links || []), ...Object.values(linksToAdd)];
  }, [packageData?.links, linksToDelete, linksToAdd]);

  if (isLoading) {
    return (
      <Layout contextSearch>
        <Header
          isLoading={{
            dimension: true,
            breadcrumb: true,
            subTitle: true,
            title: true,
            topTitle: true,
            moreDetails: true,
          }}
        />

        <div className={css.packageLinkManagement_skeleton}>
          <div className={css.skeletonSelector}>
            <SkeletonBase width={270} height={24} />
            <SkeletonBase width={300} height={36} />
          </div>

          <DimensionsSearchSkeleton
            showHeader={false}
            columns={tableColumns.length}
            rows={5}
            showPagination={false}
          />
        </div>
      </Layout>
    );
  }

  const handleDeleteLink = (link: Link) => {
    if (link?.id in linksToDelete) {
      const newLinksToDelete = structuredClone(linksToDelete);
      delete newLinksToDelete[link.id];

      setLinksToDelete(newLinksToDelete);
      return;
    }

    if (link?.id in linksToAdd) {
      const newLinksToAdd = structuredClone(linksToAdd);
      delete newLinksToAdd[link.id];

      setLinksToAdd(newLinksToAdd);
      return;
    }

    setLinksToDelete({ ...linksToDelete, [link.id]: link });
  };

  const handleSelectLink = (link: Link) => {
    if (!link?.id || !!linksToAdd?.[link.id]) {
      return;
    }

    handleLinkAssignment(true, true, [
      ...assignmentIds,
      {
        linkId: link.id,
        packageId: id,
      },
    ]);
    setLinksToAdd({ ...linksToAdd, [link.id]: link });
  };

  return (
    <Layout contextSearch>
      {isLoadingChanges && <Loading />}

      <Header
        dimension={dimension.label}
        {...(headerData ?? {})}
        breadcrumbItems={[
          {
            path: adaptedHomeUrl(),
            title: t("dimension.package.breadcrumb.previous"),
          },
          {
            path: editorUrl("package", id),
            title: t("dimension.package.breadcrumb.current"),
          },
          {
            title: t("package-link-management.title"),
          },
        ]}
        subTitle={<PackageHeaderSubtitle packageData={packageData} />}
      />

      <main className={css.packageLinkManagement}>
        <Heading className={css.packageLinkManagement_title} type="h2">
          {t("package-link-management.title")}
        </Heading>

        {isEditionEnabled && (
          <LinkSelector
            key={i18n.language}
            className={css.packageLinkManagement_linkSelector}
            placeholder={t("package-link-management.selector-placeholder")}
            label=""
            onSelectLink={handleSelectLink}
            linksToAvoid={linksToAvoidInSelector}
            keepSelection={false}
            selectedExternalOptions={linksToAdd}
            splitByGroups
            campusId={packageData?.population?.campus?.id}
            onlyCoursesGeneratingPackages
          />
        )}

        {assignmentsWithMultipleGroups > 0 && (
          <CardNotification
            className={css.infoCard}
            state="info"
            title={t("package-link-management.info-title", {
              letter: assignmentsWithMultipleGroups > 1 ? "s" : "",
            })}
            outlined
          >
            {assignmentsWithMultipleGroups > 1
              ? t("package-link-management.plural-info-message")
              : t("package-link-management.singular-info-message")}
          </CardNotification>
        )}

        <Table
          className={css.packageLinkManagement_table}
          columns={tableColumns}
          data={packagesLinks}
          rightActions={
            isEditionEnabled
              ? [
                  {
                    renderContent: link => (
                      <Tooltip
                        label={t("package-link-management.table.actions.delete-tooltip")}
                        placement="left"
                        visible={packageData?.isPublished ? false : undefined}
                      >
                        <RoundButton
                          variant="ghost"
                          size="sm"
                          disabled={packageData?.isPublished}
                          icon="circle-minus"
                          onClick={() => handleDeleteLink(link as Link)}
                        />
                      </Tooltip>
                    ),
                  },
                ]
              : undefined
          }
        />

        <LinkAssignmentErrors
          origin={origin}
          scenario={scenario}
          workspace={workspace}
          errorMessagesByType={errorMessagesByType}
        />

        <div className={css.packageLinkManagement_actions}>
          <Button disabled={isLoadingChanges} variant="ghost" onClick={handleResetChanges}>
            {isEditionEnabled
              ? t("package-link-management.btn-cancel")
              : t("package-link-management.btn-back")}
          </Button>

          {isEditionEnabled && (
            <Button
              disabled={
                isLoadingChanges || (!assignmentIds?.length && !deletionsIds?.length) || hasErrors
              }
              onClick={() => handleLinkAssignment(false, false)}
            >
              {t("package-link-management.btn-save")}
            </Button>
          )}
        </div>
      </main>
    </Layout>
  );
};

export default PackageLinkManagement;
